import { useManualSlider } from '../../hooks/useManualSlider';
import { ArrowLeftIcon } from '@/assets/icons/ArrowLeftIcon';
import { ArrowRightIcon } from '@/assets/icons/ArrowRightIcon';
import type { SectionRendererProps } from '../section-renderer.types';

interface QuoteItem {
  quote: string;
  attributionName: string;
  attributionTitle: string;
}

interface QuoteCarouselContent {
  eyebrow?: string;
  title?: string;
  subtitle?: string;
  quotes: QuoteItem[];
}

interface QuoteCardProps {
  item: QuoteItem;
  active: boolean;
  className?: string;
  /** Overridable per layout - the multi-quote carousel's quote mark is green, the single-quote side-by-side layout's is yellow (client reference screenshot). */
  quoteMarkClassName?: string;
  attributionNameClassName?: string;
  attributionTitleClassName?: string;
  /** Thin colored top edge - only the single-quote side-by-side layout has this (client reference screenshot); the carousel's cards don't. */
  topAccent?: boolean;
  /** false for the single-quote layout's full-bleed desktop panel, which fills the section edge to edge rather than floating as a rounded card. */
  rounded?: boolean;
  /** Investors' card has a 2px green border (confirmed via Figma's own get_design_context data) - every other active card has none. */
  activeBorderClassName?: string;
}

function QuoteCard({
  item,
  active,
  className = '',
  quoteMarkClassName = 'text-dkgreen',
  attributionNameClassName,
  attributionTitleClassName = 'text-dkgreen',
  topAccent = false,
  rounded = true,
  activeBorderClassName = '',
}: QuoteCardProps) {
  return (
    <div
      className={`flex flex-1 flex-col justify-between gap-4 text-left ${rounded ? 'rounded-2xl p-6' : 'p-10 lg:p-12'} ${
        active ? `bg-navy text-white shadow-lg ${activeBorderClassName}` : 'border border-slate bg-white'
      } ${topAccent ? 'border-t-4 border-t-dkgreen' : ''} ${className}`}
    >
      <div className="flex flex-col gap-4">
        <p className={`text-h1 leading-none ${quoteMarkClassName}`}>&ldquo;</p>
        <p className={`text-p5 ${active ? 'text-white' : 'text-text-secondary'}`}>{item.quote}</p>
      </div>
      <div className="flex flex-col gap-4">
        <hr className={active ? 'border-white/20' : 'border-slate/40'} />
        <div>
          <p
            className={`text-p5 font-semibold ${attributionNameClassName ?? (active ? 'text-white' : 'text-text-primary')}`}
          >
            {item.attributionName}
          </p>
          <p className={`text-p6 ${attributionTitleClassName}`}>{item.attributionTitle}</p>
        </div>
      </div>
    </div>
  );
}

interface QuoteHeaderContent {
  eyebrow?: string;
  title?: string;
  subtitle?: string;
}

// Investors' testimonial uses a different palette from Benefits' own
// single-quote section - confirmed via Figma's own get_design_context data,
// not assumed: teal eyebrow (not dkgreen), dkgreen title (not navy/
// text-primary), yellow attribution name (not white), green attribution
// title (not slate), and a 2px green card border Benefits' card doesn't
// have. Keyed by pageSlug, NOT section.key - Benefits' own single-quote
// section also happens to use the literal key "founder-experiences", so a
// key-keyed lookup here would wrongly re-color Benefits' testimonial too.
const SINGLE_QUOTE_EYEBROW_CLASS: Partial<Record<string, string>> = {
  investors: 'text-teal',
};
const SINGLE_QUOTE_TITLE_CLASS: Partial<Record<string, string>> = {
  investors: 'text-dkgreen',
};
const SINGLE_QUOTE_ATTRIBUTION_NAME_CLASS: Partial<Record<string, string>> = {
  investors: 'text-yellow',
};
const SINGLE_QUOTE_ATTRIBUTION_TITLE_CLASS: Partial<Record<string, string>> = {
  investors: 'text-green',
};
const SINGLE_QUOTE_CARD_BORDER_CLASS: Partial<Record<string, string>> = {
  investors: 'border-2 border-green',
};
// Benefits' card fills the entire right half edge to edge, per direct client
// correction (see below). Investors' own card, confirmed via Figma's own
// get_design_context data, is a contained rounded+bordered box instead -
// every other page defaults to this contained treatment too.
const FULL_BLEED_PAGES = new Set(['benefits']);

/**
 * Single-quote layout (Benefits' Real Founder Experiences), confirmed from
 * the client's reference screenshot: text on the left, one quote panel on
 * the right, distinct colors from the multi-quote carousel (near-black title
 * instead of green, yellow quote mark, muted attribution title instead of
 * green). The eyebrow itself is dkgreen here - the client's own "should be"
 * mockup shows "IN THEIR WORDS" in green, distinctly different from the
 * navy/teal "IN OTHER WORDS" in their "current Figma" comparison image right
 * next to it, even though the client's written feedback only called out the
 * text change ("In other words" -> "In Their Words"), not the color; the
 * before/after image pair is the actual color source. The panel fills the
 * ENTIRE right half of the section, edge to edge (top-to-bottom and out to
 * the section's own right edge) - not a floating rounded card with visible
 * margin - per direct client correction. Same full-bleed technique as
 * MissionSplitRenderer's image: absolutely positioned against the relative
 * `<section>`, sized as a fraction of its width.
 */
function SingleQuoteLayout({ c, quote, pageSlug }: { c: QuoteHeaderContent; quote: QuoteItem; pageSlug: string }) {
  const cardProps = {
    item: quote,
    active: true,
    quoteMarkClassName: 'text-yellow',
    attributionNameClassName: SINGLE_QUOTE_ATTRIBUTION_NAME_CLASS[pageSlug],
    attributionTitleClassName: SINGLE_QUOTE_ATTRIBUTION_TITLE_CLASS[pageSlug] ?? 'text-slate',
    activeBorderClassName: SINGLE_QUOTE_CARD_BORDER_CLASS[pageSlug],
    topAccent: true,
  } as const;
  const eyebrowClass = SINGLE_QUOTE_EYEBROW_CLASS[pageSlug] ?? 'text-dkgreen';
  const titleClass = SINGLE_QUOTE_TITLE_CLASS[pageSlug] ?? 'text-text-primary';
  const fullBleed = FULL_BLEED_PAGES.has(pageSlug);

  return (
    <section className="relative w-full overflow-hidden bg-page-bg">
      <div className="mx-auto flex w-full max-w-328 flex-col items-start gap-10 px-4 py-16 lg:flex-row lg:py-24">
        <div className="flex flex-col gap-2 text-left lg:max-w-xl">
          {c.eyebrow && <p className={`text-p6 font-semibold uppercase ${eyebrowClass}`}>{c.eyebrow}</p>}
          {c.title && <h2 className={`text-h2 ${titleClass}`}>{c.title}</h2>}
          {c.subtitle && <p className="text-p4 text-text-secondary">{c.subtitle}</p>}
        </div>
        {/* Contained card, in the normal flow - mobile/tablet always, and
          desktop too for any page not in FULL_BLEED_PAGES. */}
        <div className={`w-full ${fullBleed ? 'lg:hidden' : 'lg:w-1/2'}`}>
          <QuoteCard {...cardProps} />
        </div>
      </div>
      {fullBleed && (
        <div className="absolute inset-y-0 right-0 hidden lg:block lg:w-1/2">
          <QuoteCard {...cardProps} className="h-full" rounded={false} />
        </div>
      )}
    </section>
  );
}

interface MultiQuoteCarouselProps {
  c: QuoteHeaderContent;
  quotes: QuoteItem[];
  index: number;
  prevIndex: number;
  nextIndex: number;
  next: () => void;
  prev: () => void;
  goTo: (i: number) => void;
}

/**
 * A 3-card sliding carousel (previous/current/next, wrapping) with the
 * active quote styled distinctly (navy) from its two peeking neighbors
 * (plain white) - confirmed from a fresh Figma reference, replacing the
 * earlier single-card-with-inline-pagination design.
 */
function MultiQuoteCarousel({ c, quotes, index, prevIndex, nextIndex, next, prev, goTo }: MultiQuoteCarouselProps) {
  return (
    <section className="w-full bg-white">
      <div className="mx-auto flex w-full max-w-328 flex-col items-start gap-10 px-4 py-16">
        <div className="flex flex-col items-start gap-2 text-left">
          {/* teal (#00405C), not dkgreen - confirmed from Figma; reads as a
            dark blue, matching the client's own description of it. */}
          {c.eyebrow && <p className="text-p6 font-semibold text-teal uppercase">{c.eyebrow}</p>}
          {c.title && <h2 className="text-h2 text-dkgreen">{c.title}</h2>}
          {c.subtitle && <p className="text-p4 max-w-2xl text-text-secondary">{c.subtitle}</p>}
        </div>
        {/* Arrows sit BETWEEN the cards (prev|active|next), not outside the
          whole row - confirmed from Figma, corrected from an earlier
          side-of-the-row placement. */}
        <div className="flex w-full items-center gap-4">
          <div className="hidden flex-1 sm:block">
            <QuoteCard item={quotes[prevIndex]} active={false} />
          </div>
          <button
            type="button"
            aria-label="Previous testimonial"
            onClick={prev}
            className="flex size-10 shrink-0 cursor-pointer items-center justify-center rounded-full bg-navy text-white hover:bg-teal"
          >
            <ArrowLeftIcon size={16} />
          </button>
          <div className="flex-[1.2]">
            <QuoteCard item={quotes[index]} active />
          </div>
          <button
            type="button"
            aria-label="Next testimonial"
            onClick={next}
            className="flex size-10 shrink-0 cursor-pointer items-center justify-center rounded-full bg-navy text-white hover:bg-teal"
          >
            <ArrowRightIcon size={16} />
          </button>
          <div className="hidden flex-1 sm:block">
            <QuoteCard item={quotes[nextIndex]} active={false} />
          </div>
        </div>
        {/* self-center: the dots stay centered under the carousel row even
          though the section's own text/content now aligns left, not center. */}
        <div className="flex w-full items-center justify-center gap-2 self-center">
          {quotes.map((_, i) => (
            <button
              key={i}
              type="button"
              aria-label={`Show quote ${i + 1}`}
              onClick={() => goTo(i)}
              className={`h-2 cursor-pointer rounded-full transition-all ${
                i === index ? 'w-6 bg-dkgreen' : 'w-2 bg-slate'
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

/**
 * Quote carousel section (FR-114, FR-125) - Home's Testimonials and Benefits'
 * Real Founder Experiences. A single-quote section (Benefits' seed data
 * today) renders side-by-side (see `SingleQuoteLayout`); a multi-quote
 * section renders the sliding carousel (see `MultiQuoteCarousel`).
 */
export function QuoteCarouselRenderer({ section, pageSlug }: SectionRendererProps) {
  const c = section.content as unknown as QuoteCarouselContent;
  const quotes = c.quotes ?? [];
  const length = quotes.length;
  const { index, next, prev, goTo } = useManualSlider(length);

  if (length === 0) return null;

  if (length === 1) {
    return <SingleQuoteLayout c={c} quote={quotes[0]} pageSlug={pageSlug} />;
  }

  const prevIndex = (index - 1 + length) % length;
  const nextIndex = (index + 1) % length;

  return (
    <MultiQuoteCarousel
      c={c}
      quotes={quotes}
      index={index}
      prevIndex={prevIndex}
      nextIndex={nextIndex}
      next={next}
      prev={prev}
      goTo={goTo}
    />
  );
}
